import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * A reusable radio group component with card-style options.
 *
 * Supports single or multi-column layouts via the `columns` prop.
 * Content within each option can be vertical (label above description)
 * or horizontal (side by side).
 *
 * Supports both controlled mode (value + onChange) and uncontrolled mode (defaultValue).
 *
 * Base styles are in: base/common/_radio_group.scss
 */
export const RadioGroup = ({
  name,
  legend,
  options,
  value,
  defaultValue,
  onChange,
  columns = 1,
  contentLayout = "vertical",
  required = false,
  className = "",
}) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;

  const handleItemClick = (optionValue) => {
    if (!isControlled) {
      setInternalValue(optionValue);
    }
    if (onChange) {
      onChange(optionValue);
    }
  };

  const columnsModifier = columns > 1 ? `radio-group-items--columns-${columns}` : "";
  const contentHorizontalModifier =
    contentLayout === "horizontal" ? "radio-group-item-content--horizontal" : "";

  return (
    <fieldset className={`radio-group ${className}`.trim()}>
      <legend className="radio-group-legend">{legend}</legend>
      <div className={`radio-group-items ${columnsModifier}`.trim()}>
        {options.map((opt) => {
          const isSelected = currentValue === opt.value;
          const radioId = `${name}-${opt.value}`;
          return (
            <div
              key={opt.value}
              className="radio-group-item"
              onClick={() => handleItemClick(opt.value)}
            >
              <input
                id={radioId}
                type="radio"
                name={name}
                value={opt.value}
                checked={isSelected}
                onChange={() => handleItemClick(opt.value)}
                className="radio-group-item-radio"
                required={required}
              />
              <span
                className={`radio-group-item-content ${contentHorizontalModifier}`.trim()}
              >
                <label htmlFor={radioId} className="radio-group-item-content-label">
                  {opt.label}
                </label>
                <span className="radio-group-item-content-description">
                  {opt.description}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

RadioGroup.propTypes = {
  /** Unique name for the radio group (used for form submission and accessibility) */
  name: PropTypes.string.isRequired,
  /** Legend text displayed as the section title */
  legend: PropTypes.string.isRequired,
  /** Array of options with value, label, and description */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /** Currently selected value (controlled mode) */
  value: PropTypes.string,
  /** Initial value (uncontrolled mode) */
  defaultValue: PropTypes.string,
  /** Callback when selection changes, receives the new value */
  onChange: PropTypes.func,
  /** Number of columns for the layout (1 = vertical list, 2+ = grid) */
  columns: PropTypes.number,
  /** Layout of label/description within each option: "vertical" or "horizontal" */
  contentLayout: PropTypes.oneOf(["vertical", "horizontal"]),
  /** Whether a selection is required */
  required: PropTypes.bool,
  /** Additional CSS class name for the fieldset */
  className: PropTypes.string,
};
